import React, { createContext, useState, useEffect, useRef } from "react";
import { collection, doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useAuth } from "./AuthContext";

export const UsuariosContext = createContext({ usuarios: [], loading: true });

export const UsuariosProvider = ({ children }) => {
  const { user, orgId } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ cache persistente mientras la app está montada
  const profileCacheRef = useRef(new Map());

  useEffect(() => {
    if (!user) {
      profileCacheRef.current.clear();
      setUsuarios([]);
      setLoading(false);
      return;
    }

    if (!orgId) {
      profileCacheRef.current.clear();
      setUsuarios([]);
      setLoading(false);
      return;
    }

    // ✅ cada vez que cambie la org, limpiamos cache para no mezclar data
    profileCacheRef.current.clear();

    const currentOrgId = orgId;
    setLoading(true);

    let alive = true;

    const membersCol = collection(db, "orgs", currentOrgId, "members");

    const unsubscribeMembers = onSnapshot(
      membersCol,
      async (snap) => {
        try {
          const members = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

          const enriched = await Promise.all(
            members.map(async (m) => {
              const uid = m.id;

              // 1) cache
              const cached = profileCacheRef.current.get(uid);
              if (cached) {
                return {
                  ...m,
                  uid,
                  nombre: cached.nombre || "",
                  apellido: cached.apellido || "",
                  email: cached.email || "",
                  avatarUrl: cached.avatarUrl || "",
                  registrado: true,
                  role: m.role || m.rol || "",
                  rol: m.role || m.rol || "", // compat UI (si aún lo usa)
                };
              }

              // 2) leer perfil
              try {
                const uSnap = await getDoc(doc(db, "users", uid));
                const u = uSnap.exists() ? uSnap.data() : null;

                if (u) profileCacheRef.current.set(uid, u);

                return {
                  ...m,
                  uid,
                  nombre: u?.nombre || "",
                  apellido: u?.apellido || "",
                  email: u?.email || "",
                  avatarUrl: u?.avatarUrl || "",
                  registrado: !!uSnap.exists(),
                  role: m.role || m.rol || "",
                  rol: m.role || m.rol || "",
                };
              } catch {
                return {
                  ...m,
                  uid,
                  nombre: "",
                  apellido: "",
                  email: "",
                  avatarUrl: "",
                  registrado: false,
                  role: m.role || m.rol || "",
                  rol: m.role || m.rol || "",
                };
              }
            }),
          );

          if (!alive) return;
          setUsuarios(enriched);
          setLoading(false);
        } catch (err) {
          console.error("Error procesando members:", err);
          if (!alive) return;
          setLoading(false);
        }
      },
      (err) => {
        console.error("Error en onSnapshot members:", err);
        if (!alive) return;
        setLoading(false);
      },
    );

    return () => {
      alive = false;
      unsubscribeMembers();
    };
  }, [user, orgId]);

  return (
    <UsuariosContext.Provider value={{ usuarios, loading }}>
      {children}
    </UsuariosContext.Provider>
  );
};
